import { z } from 'zod';

// Contractele Elsei (etapa 6, docs/22): parametrii cautarii, regulile alertelor, planurile.
// Aceleasi reguli ca in migrarea 20261105100000_elsa.sql (private.seap_where,
// private.valid_watchlist_rules, admin_api.upsert_plan); listele de chei sunt verificate de
// test/sql-sync.test.ts (marcajele contracts:SEARCH_KEYS si contracts:WATCHLIST_RULE_KEYS).

export const SEARCH_KEYS = [
  'q',
  'keywords',
  'cpv',
  'cpv_prefixes',
  'category',
  'categories',
  'county',
  'counties',
  'authority_id',
  'supplier_id',
  'day_from',
  'day_to',
  'value_min',
  'value_max',
  'cursor',
  'limit',
] as const;

export const WATCHLIST_RULE_KEYS = [
  'keywords',
  'cpv_prefixes',
  'categories',
  'counties',
  'authority_id',
  'value_min',
  'value_max',
] as const;

/** Prefixul unui cod CPV, ca private.cpv_prefix(): fara cifra de control si fara zerourile de
 *  la coada („33690000-3” -> „3369”). Lungimea da nivelul: 2 = domeniu, 3 = grupa, 4 = clasa. */
export function cpvPrefixOf(code: string): string {
  const d = (code.trim().split('-')[0] ?? '').replace(/0+$/, '');
  return d.length < 2 ? d.padEnd(2, '0') : d;
}

/** Cate cifre trebuie sa aiba un cod ales de client (clasa sau mai fin); domeniile le punem noi. */
export const CLIENT_CPV_MIN_DIGITS = 4;

/** Un prefix CPV: 2-8 cifre (fara cifra de control: „33141110-4” devine „33141110”). */
export const CpvPrefix = z
  .string()
  .trim()
  .transform((s) => s.split('-')[0] ?? '')
  .pipe(z.string().regex(/^[0-9]{2,8}$/, 'Codul CPV are 2-8 cifre'));

const Day = z.iso.date();
const Keyword = z.string().trim().min(1).max(200);
const Category = z.number().int().min(0).max(12);
const County = z.string().trim().min(1).max(60);
const Money = z.number().nonnegative();
const Id = z.number().int().positive();

export const SearchCursor = z.object({ day: Day, id: Id });
export type SearchCursor = z.infer<typeof SearchCursor>;

export const SearchParams = z
  .strictObject({
    q: z.string().trim().max(200).optional(),
    keywords: z.array(Keyword).max(20).optional(),
    cpv: CpvPrefix.optional(),
    cpv_prefixes: z.array(CpvPrefix).max(50).optional(),
    category: Category.optional(),
    categories: z.array(Category).max(13).optional(),
    county: County.optional(),
    counties: z.array(County).max(50).optional(),
    authority_id: Id.optional(),
    supplier_id: Id.optional(),
    day_from: Day.optional(),
    day_to: Day.optional(),
    value_min: Money.optional(),
    value_max: Money.optional(),
    cursor: SearchCursor.optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .refine((p) => !(p.day_from && p.day_to && p.day_from > p.day_to), {
    message: 'Data de început e după data de sfârșit',
    path: ['day_to'],
  });
export type SearchParams = z.infer<typeof SearchParams>;

export const WatchlistRules = z
  .strictObject({
    keywords: z.array(Keyword).max(20).optional(),
    cpv_prefixes: z.array(CpvPrefix).max(50).optional(),
    categories: z.array(Category).max(13).optional(),
    counties: z.array(County).max(50).optional(),
    authority_id: Id.optional(),
    value_min: Money.optional(),
    value_max: Money.optional(),
  })
  .refine(
    (r) =>
      (r.keywords?.length ?? 0) > 0 ||
      (r.cpv_prefixes?.length ?? 0) > 0 ||
      (r.categories?.length ?? 0) > 0 ||
      r.authority_id !== undefined,
    {
      message:
        'Alerta are nevoie de cel puțin un cuvânt cheie, un cod CPV, o categorie sau o autoritate',
    },
  );
export type WatchlistRules = z.infer<typeof WatchlistRules>;

export const WATCHLIST_SCOPES = ['personal', 'team'] as const;
export const WatchlistScope = z.enum(WATCHLIST_SCOPES);

/** Cate randuri intoarce un export (app.export_rows), in limita plafonului lunar. */
export const EXPORT_MAX_ROWS = 10_000;
export const EXPORT_KINDS = ['acq', 'item'] as const;
export const ExportKind = z.enum(EXPORT_KINDS);

export const BILLING_INTERVALS = ['month', 'year'] as const;
export const BillingInterval = z.enum(BILLING_INTERVALS);
export type BillingInterval = z.infer<typeof BillingInterval>;

const StripePrice = z
  .string()
  .trim()
  .regex(/^price_[A-Za-z0-9_]+$/, 'Id-ul unui preț Stripe începe cu price_');

/** Un plan, cum il trimite Pluto la admin_api.upsert_plan. null la un drept = nelimitat. */
export const PlanInput = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/, 'Litere mici, cifre și _ (2-31)'),
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  price_monthly_ron: z.number().nonnegative().nullable().optional(),
  price_yearly_ron: z.number().nonnegative().nullable().optional(),
  stripe_price_monthly: StripePrice.nullable().optional(),
  stripe_price_yearly: StripePrice.nullable().optional(),
  is_public: z.boolean(),
  sort: z.number().int().min(0).max(1000).optional(),
  features: z.object({
    seats: z.number().int().min(1).max(1000),
    history_days: z.number().int().min(1).nullable(),
    max_watchlists: z.number().int().min(0).nullable(),
    export_rows_month: z.number().int().min(0).nullable(),
    api_access: z.boolean(),
    cpv_codes: z.number().int().min(1).nullable(),
  }),
});
export type PlanInput = z.infer<typeof PlanInput>;
